import React, { useState, useContext, useCallback } from 'react'
import { useNavigate, Routes, Route } from 'react-router-dom'
import { AuthContext } from '@/layout/Auth'
import { Row } from '../components'
import { PlainCard } from '../components'
import Table, { pageProps } from '@/components/Table'
import { ConfirmButton } from '@/components/Button'
import { MemberInfo } from '@/types'
import { memberColumns } from './column'
import { KeywordSearch } from '@/components/Search'
import InvitationList from './inviteForm'
import { ColumnAction } from '@/components/Table'
import { useDebounce } from '@/hooks'
import { getMemebers, deleteMember } from '@/api'
import { useQuery, useMutation } from '@tanstack/react-query'
import { confirm } from '@/components/Modal'
import Editor from './editor'
import ErrorBoundary from '@/layout/Error'
import { ensureSecurityGate } from '@/utils/securityGate'

const Member: React.FC<unknown> = () => {
  const navigate = useNavigate()
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo

  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState<MemberInfo[]>([])
  const [rows, setRows] = useState<number>(10)

  const [keyword, setKeyword] = useState<string>('')
  const debounceInput = useDebounce(keyword, 500)
  // @ts-ignore
  const [editMember, setEditing] = useState<MemberInfo>(null)

  const queryMembers = useQuery(
    ['getMemebers', orgInfo?.id, page, rows, debounceInput],
    () =>
      // @ts-ignore
      orgInfo?.id &&
      getMemebers({
        id: orgInfo?.id,
        query: { page, size: rows, nickname: debounceInput },
      }),
    {
      onSuccess: (res) => {
        // @ts-ignore
        setTotal(res.total)
        // @ts-ignore
        setData(res.data)
      },
      refetchOnMount: true,
      enabled: !!orgInfo?.id,
    },
  )

  const deleteMemberMutation = useMutation(deleteMember, {
    onSuccess: () => {
      queryMembers.refetch()
    },
  })

  const handlePageChange = (value: pageProps) => {
    setPage(value.page)
    // @ts-ignore
    setRows(value.rowsPerPage)
  }

  const columnsAction = (action: ColumnAction, data: MemberInfo) => () => {
    switch (action) {
      case 'delete': {
        confirm({
          title: '提示',
          content: `确认将用户"${
            data?.nickname?.length > 10 ? `${data?.nickname?.slice(0, 10)}...` : data?.nickname
          }"从空间"${orgInfo?.name}"中移除`,
          // @ts-ignore
          onClose: null,
          onConfirm: async () => {
            if (!(await ensureSecurityGate('admin_operation'))) return
            deleteMemberMutation.mutate({
              // @ts-ignore
              id: orgInfo?.id,
              query: { id: data?.id as string },
            })
          },
          closeAfterClose: true,
        })
        break
      }
      case 'edit': {
        ;(async () => {
          if (!(await ensureSecurityGate('admin_operation'))) return
          setEditing(data)
          gotoEdit()
        })()
        break
      }
    }
  }

  const goToInvite = useCallback(() => {
    ;(async () => {
      if (!(await ensureSecurityGate('admin_operation'))) return
      navigate('/console/organization/member/inviteMember')
    })()
  }, [navigate])

  const goToMemberList = useCallback(() => {
    // @ts-ignore
    setEditing(null)
    orgInfo?.id && queryMembers.refetch()
    navigate('/console/organization/member')
  }, [navigate, orgInfo?.id, queryMembers])

  const gotoEdit = useCallback(() => {
    navigate('/console/organization/member/editMember')
  }, [navigate])

  return (
    <PlainCard>
      <Routes>
        <Route
          path='/'
          errorElement={<ErrorBoundary />}
          element={
            <>
              <Row sx={{ justifyContent: 'flex-end', gap: '24px' }}>
                <KeywordSearch
                  placeholder='搜索用户名'
                  clear={true}
                  value={keyword}
                  onChange={setKeyword}
                  // @ts-ignore
                  onSearch={null}
                />
                <ConfirmButton onClick={goToInvite}>添加用户</ConfirmButton>
              </Row>
              <Table<MemberInfo>
                sx={{ flex: 1 }}
                columns={memberColumns(columnsAction)}
                data={data}
                rowKey={'id'}
                pagination={{
                  page,
                  total,
                  onChange: handlePageChange,
                  rowsPerPage: rows,
                }}
              />
            </>
          }
        />
        <Route
          path='inviteMember'
          errorElement={<ErrorBoundary />}
          element={<InvitationList onCancel={goToMemberList} />}
        />
        <Route
          path='editMember'
          errorElement={<ErrorBoundary />}
          element={
            <Editor
              refetch={queryMembers.refetch}
              userInfo={editMember}
              // @ts-ignore
              orgInfo={orgInfo}
              onCancel={goToMemberList}
            />
          }
        />
      </Routes>
    </PlainCard>
  )
}

export default Member
